import { Flex, Heading, Text, useToast } from '@afterpaytouch/core'
import React, { useEffect } from 'react'
import { useFlag } from '@afterpaytouch/feature'
import { Head, LoginIdentity, Promotion, SocialBanner, SocialBannerUk } from '../../../src/component'
import { Route } from '../../../src/config/router'
import { CheckoutError, CheckoutPage, Features, isPublicResponse } from '../../../src/model'
import {
  clearConsumer,
  clearSessionExpired,
  getErrorFromPublicResponseWithSource,
  PublicResponseWithSource,
  resetRegistrationState,
  setCheckoutError,
  useAppDispatch,
  useAppRouter,
  useConsumerSelector,
  useIsAppleRetailOrder,
  useMerchantId,
} from '../../../src/state'
import { useCheckoutSizes } from '../../../src/utils/responsive'
import { TrackingEvent } from '../../../src/model/monitoring'
import { PromotionType } from '../../../src/component/Promotion/constants'
import { useLookupSuccessHandler } from '../../../src/utils/lookup'
import { TrackPageViewed, useMonitoringWithEnduringEventProperties } from '../../../src/integrations/monitoring'
import { useTranslation, useUpdateAfterpayJsBrand } from '../../utils/convergence'

export const LoginPage: CheckoutPage = () => {
  useUpdateAfterpayJsBrand()
  const { t } = useTranslation()
  const router = useAppRouter()
  const { sessionExpiryUrl } = useConsumerSelector()
  const dispatch = useAppDispatch()
  const { headingSize, textSize } = useCheckoutSizes()
  const setToast = useToast()
  const merchantId = useMerchantId()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const onLookupSuccess = useLookupSuccessHandler()
  const testTargeting = useFlag(Features.VERIFY_SERVICE_TEST_TARGETING)
  const isAppleRetailOrder = useIsAppleRetailOrder()

  const pageTitle = `${t('common:brandName')} | ${t('login:getStarted')}`

  const onError = (error: CheckoutError | PublicResponseWithSource): void => {
    if (isPublicResponse(error)) {
      dispatch(setCheckoutError(getErrorFromPublicResponseWithSource(error)))
    } else {
      dispatch(setCheckoutError(error))
    }
    router.push(Route.TERMINAL_ERROR)
  }

  useEffect(() => {
    router.prefetch(Route.ENTER_PASSWORD)
    router.prefetch(Route.VERIFY)
    router.prefetch(Route.SIGN_UP)
    router.prefetch(Route.TERMINAL_ERROR)
    router.prefetch(Route.VERIFY_CHALLENGE)
    dispatch(resetRegistrationState())
    dispatch(clearConsumer())

    logEvent(TrackingEvent.SHOW_TEST_TARGETING, { testTargeting })

    const sessionExpiredMessage = (): JSX.Element => (
      <>
        <Text size={textSize} bold>
          {t('common:sessionExpiredMessage')}
        </Text>
        <Text size={textSize}>{t('common:sessionExpiredSubMessage')}</Text>
      </>
    )

    if (sessionExpiryUrl) {
      setToast({
        message: sessionExpiredMessage,
        onShow: () => {
          logEvent(TrackingEvent.VIEWED_CHECKOUT_TIMEOUT, {
            url: sessionExpiryUrl,
          })

          dispatch(clearSessionExpired())
        },
        kind: 'Failure',
      })
    }
  }, [])

  return (
    <>
      <Head title={pageTitle} />
      {isAppleRetailOrder ? (
        <Flex direction='column' className='flex-gap'>
          <Heading size={headingSize}>{t('login:logInOrGetStarted')}</Heading>
          <Text size={textSize}>{t('login:logInSubtitle')}</Text>
        </Flex>
      ) : (
        <Heading size={headingSize}>{t('login:getStarted')}</Heading>
      )}
      <LoginIdentity onSuccess={onLookupSuccess} onError={onError} />
      <Promotion merchantId={merchantId} promotionType={PromotionType.NEW_USER_PROMO} />
      <SocialBanner />
      <SocialBannerUk />
      <TrackPageViewed />
    </>
  )
}
